import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SustainabilityDashboardPromo from '@src/components/DisposalPage/SustainabilityDashboardPromo'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/toxic-waste-disposal-page.module.css'
// eslint-disable-next-line import/extensions
import toxicWaste from '../../json/toxic-waste-disposal-data.json'

const toxicServiceData = {
  name: 'Toxic Waste Disposal',
  description:
    'Compliant toxic waste disposal for businesses. We handle heavy metals (lead, mercury, cadmium, chromium, arsenic), pesticides, toxic solvents, sludges, and contaminated debris carrying the RCRA toxicity characteristic, with full EPA documentation.',
  serviceType: 'Toxic Waste Disposal',
  url: 'https://getdimension.com/toxic-waste-disposal',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Heavy Metal Waste Disposal' },
    { name: 'Pesticide and Herbicide Disposal' },
    { name: 'Toxic Solvent Disposal' },
    { name: 'Toxic Sludge Disposal' },
    { name: 'Contaminated Soil and Debris Disposal' },
  ],
}

const toxicFAQData = {
  questions: toxicWaste.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function ToxicWasteDisposal() {
  return (
    <>
      <Head>
        <title>Toxic Waste Disposal Services for Businesses | Dimension</title>
        <meta
          name="description"
          content="Compliant toxic waste disposal for businesses — heavy metals, pesticides, toxic solvents, sludges, contaminated debris. RCRA toxicity characteristic, instant quote, 30% less than competitor rates."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/toxic-waste-disposal" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={toxicServiceData} />
        <SchemaMarkup type="FAQPage" data={toxicFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heading={toxicWaste.pitch.heading}
          material={toxicWaste.pitch.material}
          pitchText={toxicWaste.pitch.pitchText}
          ctaLink={toxicWaste.pitch.ctaLink}
          ctaText={toxicWaste.pitch.ctaText}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Clients />
        <FeatureList {...toxicWaste.solutions} />
        <SustainabilityDashboardPromo />
        <FeatureList {...toxicWaste.benefits} />
        <CaseStudy {...toxicWaste.caseStudy} />
        <Approach {...toxicWaste.approach} />
        <FAQ {...toxicWaste.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Hazardous Waste Disposal',
              href: '/hazardous-waste-disposal',
              description:
                'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
            },
            {
              label: 'Corrosive Waste Disposal',
              href: '/corrosive-waste-disposal',
              description:
                'Compliant disposal of waste acids, caustics, and other DOT Class 8 corrosive liquids.',
            },
            {
              label: 'Regulated Waste Disposal',
              href: '/regulated-waste-disposal',
              description:
                'The catch-all for EPA- and DOT-regulated materials — universal waste, batteries, aerosols, gases, and more.',
            },
            {
              label: 'Lab Pack Services',
              href: '/lab-pack-services',
              description:
                'Chemist-led identification, segregation, and pack-out of small-container laboratory chemicals.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default ToxicWasteDisposal
